import { useState, useCallback } from "react";
import { useHistory } from "react-router";
import { API } from "aws-amplify";
import {
  benchmarkRequests,
  updateUserSettings,
} from "../../../../graphql/mutations";
import { AnimalType } from "../../../../graphql/types";
import useAdobeDataLayer from "../../analytics/adobeDataLayer";
import featureFlags from "../../utils/featureFlags";
import { SampleType } from "./AccessSampleData";

interface UseAccessSampleDataProps {
  customerID: string;
  onClose?: () => void;
}

interface SaveSampleDataParams {
  sampleDataType?: SampleType;
  animalType?: AnimalType;
  regions?: string[];
  doNotShowAgain?: boolean;
}

export enum Region {
  ALL = "ALL",
  BRAZIL = "BRAZIL",
  FRANCE = "FRANCE",
  NETHERLANDS = "NETHERLANDS",
  SPAIN = "SPAIN",
}

export const ANIMAL_TYPES_WITH_SAMPLE_DATA: readonly AnimalType[] = [
  AnimalType.Beef,
  AnimalType.BroilerV2,
  AnimalType.LayingHensV2,
  AnimalType.Pig,
  AnimalType.SalmonV2,
  AnimalType.SeaBass,
  AnimalType.SeaBream,
  AnimalType.Turkey,
];

export const ANIMAL_TYPES_REQUIRING_REGIONS: readonly AnimalType[] = [
  AnimalType.Pig,
  AnimalType.BroilerV2,
];

export const SAMPLE_DATA_REGIONS_BY_ANIMAL: Partial<
  Record<AnimalType, Region[]>
> = {
  [AnimalType.Pig]: [
    Region.ALL,
    Region.BRAZIL,
    Region.FRANCE,
    Region.NETHERLANDS,
    Region.SPAIN,
  ],
  [AnimalType.BroilerV2]: [Region.NETHERLANDS],
};

export const getRegionsForAnimalType = (animal: AnimalType) =>
  SAMPLE_DATA_REGIONS_BY_ANIMAL[animal] ?? [];

export const requiresRegionSelection = (animal: AnimalType) =>
  getRegionsForAnimalType(animal).length > 0;

const getFeedRoute = (customerID: string, isV2Routing = false) =>
  isV2Routing
    ? `/v2/feeds?customerId=${customerID}`
    : `/customers/${customerID}/myfeeds`;

const getFarmRoute = (customerID: string, isV2Routing = false) =>
  isV2Routing
    ? `/v2/farms?organizationId=${customerID}`
    : `/customers/${customerID}/myfarms`;

const useAccessSampleData = ({
  customerID,
  onClose,
}: UseAccessSampleDataProps) => {
  const history = useHistory();
  const { formSubmissionEvent } = useAdobeDataLayer();

  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const onSuccess = (sampleDataType?: SampleType) => {
    if (sampleDataType) {
      if (sampleDataType === SampleType.FEED) {
        history.push(getFeedRoute(customerID, featureFlags.feedV2Routing));
      } else if (sampleDataType === SampleType.FARM) {
        history.push(getFarmRoute(customerID, featureFlags.farmV2Routing));
      }
    }
    onClose?.();
  };

  const updateDoNotShowSettings = async (): Promise<void> => {
    try {
      await API.graphql({
        query: updateUserSettings,
        variables: {
          input: { sampleDataAccessPopup: true },
          segment: "BENCHMARKS",
        },
      });
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (err.errors?.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const error = err.errors[0] as Error;
        setErrorMessage(error.message);
      }
      setErrorMessage("Something went wrong.");
    }
  };

  const saveSampleDataRequest = async ({
    sampleDataType,
    animalType,
    regions = [],
    doNotShowAgain = false,
  }: SaveSampleDataParams): Promise<void> => {
    setErrorMessage("");
    setSaving(true);

    try {
      if (doNotShowAgain && !sampleDataType && !animalType) {
        await updateDoNotShowSettings();
        onClose?.();
        return;
      }

      await formSubmissionEvent("Request sample data", "add", animalType);

      await API.graphql({
        query: benchmarkRequests,
        variables: {
          input: {
            type: sampleDataType,
            animalType,
            regions,
          },
          customerId: customerID,
        },
      });

      if (doNotShowAgain) {
        await updateDoNotShowSettings();
      }

      onSuccess(sampleDataType);
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (err.errors?.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const error = err.errors[0] as Error;
        setErrorMessage(error.message);
      }
      setErrorMessage("Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  const clearError = useCallback(() => {
    setErrorMessage("");
  }, []);

  return {
    saving,
    errorMessage,
    saveSampleDataRequest,
    clearError,
  };
};

export default useAccessSampleData;
